import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { chooseLocale } from "../i18n";
import { LANGUAGE_NAMES, LOCALES } from "../i18n/locale";
import { api } from "../lib/api";
import type { User } from "../lib/types";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { useAuth, useCan } from "../auth/AuthProvider";
import { AFFILIATE_URL } from "../lib/affiliate";
import { useBilling } from "../lib/queries";
import { siteUrl } from "../lib/site";
import { GTM_ID, openConsentPreferences } from "../lib/gtm";
import { absoluteDay } from "../lib/format";
import { formatMoney } from "../lib/money";
import { AnnouncementBanner } from "./AnnouncementBanner";
import { CommandPalette, isPaletteChord, paletteShortcutLabel } from "./CommandPalette";
import { Icon, LogoMark, type IconName } from "./icons";
import { Dropdown, buttonStyles, cx } from "./ui";
import type nav from "../locales/en/nav.json";

/** The rows the rail can hold, which is exactly what `nav.rail` names. */
export type NavKey = keyof typeof nav.rail;

const SIDEBAR_WIDTH = 260;

/*
 * Navigation items grouped by purpose. Apps and databases are things you build;
 * the rest configure the organization around them.
 *
 * Team belongs here and not in the user menu. It is scoped to the organization,
 * so switching orgs changes what it means — filing it under the signed-in
 * account said the opposite, two clicks deep. Security stays in the user menu,
 * because a password and a second factor really are the account's.
 */
export interface NavItem {
  to: string;
  /**
   * The row's name under `nav.rail`, not the name itself.
   *
   * This was the English label, which made the rail's contents copy embedded in
   * a module three other files import. A key is the row's identity — stable
   * across languages, which is what `test/nav-rail.test.ts` is actually
   * asserting about when it checks the order and the grouping.
   */
  key: NavKey;
  icon: IconName;
  /** Off this app entirely: rendered as an anchor, never as a current page. */
  external?: boolean;
  /**
   * Draw a rule above this row, opening a new group.
   *
   * On the item rather than in a set of indices beside the list. The previous
   * version held positions, so inserting a row silently moved every boundary
   * after it — which is exactly what happened when Tunnels landed between
   * Databases and Schedules, splitting the workloads and filing API keys with
   * AI. A flag travels with the row it belongs to and cannot drift.
   */
  startsGroup?: boolean;
  /**
   * A heading over the group this row opens, looked up under `railGroups`.
   *
   * Six product rows in one unbroken run read as a list to be scanned top to
   * bottom; three under "Run" and three under "Connect" read as two shapes.
   * The heading says what the rows below it have in common, which is the
   * thing the order alone was trying to say and could not once there were
   * six of them.
   */
  group?: "run" | "connect" | "organization";
}

export const NAV_ITEMS: NavItem[] = [
  /*
   * The three things this platform runs for you, then the two that extend
   * them.
   *
   * Tunnels sat directly under Databases on the reasoning that it was "a
   * database in your own network". That was the same narrowing since taken out
   * of the copy and the docs: a tunnel forwards TCP to whatever is listening —
   * a broker, an internal API, a mainframe — and filing it under the database
   * it most often reaches taught everybody the wrong shape from the rail
   * outward.
   *
   * So the order is what each row is, not what it is often used with. Apps,
   * Databases and AI Inference are workloads running here. Schedules and
   * Tunnels attach to them: a job inside an app, a route out to somewhere this
   * platform does not run. Tunnels is last because it is the only row that is
   * mostly about somewhere else.
   */
  { to: "/apps", key: "apps", icon: "apps", group: "run" },
  { to: "/databases", key: "databases", icon: "database" },
  // Above the divider, with the rest: inference is a thing you build with, not
  // a thing you configure. It is billed from the same credit as everything else
  // in this group, which is the other half of why it belongs in it.
  //
  // "AI Inference" rather than "AI", which named a field rather than the thing
  // on the page — every other row in this rail is a noun you can create one of.
  { to: "/ai", key: "ai", icon: "models" },
  // With the workloads above rather than under Settings: a scheduled job is a
  // thing you create and pay for, not a preference you set.
  { to: "/schedules", key: "schedules", icon: "clock", startsGroup: true, group: "connect" },
  /*
   * Named for the mechanism rather than "Connections", which competed with
   * Integrations, the account menu's name for git connections, inside one
   * product.
   */
  { to: "/tunnels", key: "tunnels", icon: "lock" },
  /*
   * Last of the things you create and pay for. A domain is bought rather than
   * metered, and the registrar hosts it rather than a server — which is why it
   * closes the group instead of sitting beside apps: everything above it runs.
   */
  { to: "/domains", key: "domains", icon: "globe" },
  { to: "/settings/api-keys", key: "apiKeys", icon: "key", startsGroup: true, group: "organization" },
  { to: "/settings/team", key: "team", icon: "team" },
  { to: "/settings/billing", key: "billing", icon: "card" },
  /*
   * The referral programme, in a group of its own.
   *
   * Not with Billing above, although both are about money: Billing configures
   * this organization, and this is the person's own — an affiliate account
   * belongs to whoever signs in, whichever organization the switcher holds. Not
   * with Support below either, which is the way out of a problem.
   *
   * A page here rather than an anchor straight to the affiliate dashboard: the
   * row is how somebody who has never heard of the programme finds out what it
   * pays, and sending them to a sign-in screen to learn that asks for a
   * decision before giving a reason. Only where the deployment runs one.
   */
  ...(AFFILIATE_URL
    ? [{ to: "/referrals", key: "referrals", icon: "gift", startsGroup: true } as const]
    : []),
  /*
   * Support closes the rail, below a rule of its own.
   *
   * It is org-scoped like the group above it — a ticket belongs to the
   * organization, not to whoever opened it — but it is not a thing you
   * configure, and filing it with API keys and Billing would say it was. Last
   * is also where people look for it: it is the row you want when the rest of
   * the product has not worked.
   */
  { to: "/support", key: "support", icon: "support", startsGroup: true },
  /*
   * The documentation, which lives on the marketing site.
   *
   * Under Support rather than above it, because the rail is ordered by what
   * somebody is looking for: the row you want when the product has not worked,
   * then the row you want when you are not sure how it works. Reading the docs
   * is also the answer to a good share of the tickets below it, which is the
   * other argument for the two being neighbours.
   *
   * It leaves the app, so it says so — an anchor with the outbound glyph and no
   * active state, rather than a nav item that silently takes you off the
   * dashboard.
   */
  { to: "/docs", key: "docs", icon: "book", external: true },
];

/*
 * One row style for every item in either dropdown, so the org switcher and the
 * account menu do not drift apart the way they had.
 *
 * The hover is a translucent lift rather than `bg-surface`, which is *darker*
 * than the `raised` panel these sit on — the same reason admin's rail overlays
 * are `bg-white/5`. There is no token for "one step up from wherever I am",
 * because the semantic scale is absolute.
 */
const MENU_ITEM =
  "flex w-full items-center gap-2 rounded-sm px-2.5 py-1.5 text-left text-[13px] text-fg-muted transition-colors duration-200 ease-smooth hover:bg-fg/8 hover:text-fg";

export function AppLayout() {
  const { t, i18n } = useTranslation("nav");
  const { user, organizations, currentOrg, selectOrg, logout, reloadSession } = useAuth();
  const canCreate = useCan("developer");
  const billing = useBilling(currentOrg?.id);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();

  /*
   * The same three steps the profile page takes, in the same order: the account
   * first, because a session load applies `users.locale` over whatever the
   * cookie says and a choice saved only in the cookie would revert on the next
   * visit. If the save fails the language still changes here and now — the
   * menu has nowhere to show an error — and the profile page is where it can
   * be saved again with one.
   */
  async function pickLocale(locale: string) {
    if (locale === i18n.language) return;
    let saved = true;
    try {
      await api<User>("/v1/auth/me", { method: "PATCH", body: { locale } });
    } catch {
      saved = false;
    }
    await chooseLocale(locale);
    if (saved) await reloadSession();
  }

  // Owned here rather than in the palette so the sidebar's search button and
  // the chord open the same thing.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!isPaletteChord(event)) return;
      event.preventDefault();
      setPaletteOpen((value) => !value);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Navigating from inside the drawer should close it; otherwise it stays open
  // over the page you just asked for.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Escape closes the drawer, matching every other overlay in the app.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  /*
   * Lock the page while the drawer is over it. Without this a swipe meant for a
   * long nav list scrolled the page underneath instead — measured at 111px on a
   * 390px viewport — so you closed the drawer and found yourself somewhere you
   * had not chosen to go.
   *
   * The operator console already did this; this one did not, which is the kind
   * of gap that only shows up when the two are driven side by side.
   */
  useEffect(() => {
    if (!drawerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [drawerOpen]);

  const sidebar = (
    <div className="flex h-full flex-col bg-surface px-3 overflow-visible">
      {/* Just the wordmark. Organization switching moved into the account menu
          at the foot of the rail, so the head of it is one thing again. */}
      <div className="w-full flex-shrink-0 border-b border-line pt-4 pb-4">
        <NavLink to="/apps" className="flex min-w-0 items-center gap-2.5 pointer-coarse:min-h-11">
          <LogoMark className="size-7 flex-shrink-0" />
          <span className="truncate text-sm font-semibold tracking-tight text-fg">Maxlayer</span>
        </NavLink>
      </div>

      {/* Search, then the one create action. The search row comes first because
          on an org with forty apps it is the more frequent of the two. */}
      <div className="w-full flex-shrink-0 space-y-2 py-3">
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="flex h-8 pointer-coarse:h-11 w-full items-center gap-2 rounded-md border border-line px-2.5 text-left text-[13px] text-fg-subtle transition-colors duration-200 ease-smooth hover:border-line-strong hover:bg-raised hover:text-fg-muted"
        >
          <Icon name="search" className="size-3.5 shrink-0" />
          <span className="flex-1 truncate">{t("search")}</span>
          <kbd className="shrink-0 font-mono text-[10px] tracking-tight">
            {paletteShortcutLabel()}
          </kbd>
        </button>

        {canCreate && (
          /*
             A menu rather than a link, because "new" stopped meaning one thing.
             This was hardcoded to `/apps/new` from when an app was the only
             thing a customer could make — so the one global create action named
             half the product and a database was reachable only from its own
             page or the command palette, which is where the people who already
             knew were looking anyway.
             (It was a hand-rolled `bg-fg text-surface` block before that, the
             third distinct primary button in the app; the trigger keeps the
             variant that replaced it.)

             Both items sit behind the same `canCreate`, and that is a fact
             rather than a shortcut: `AppsPage` and `DatabasesPage` each gate
             their own button on `useCan("developer")` too. If they ever diverge,
             this menu has to gate each item on its own or it will offer
             somebody a page that refuses them.

             The order is apps, databases, schedules — matching the nav above it.
             A database is attached to something, so the thing it attaches to
             reads first, and a schedule runs inside something, so it reads
             after both.
          */
          <Dropdown
            align="stretch"
            trigger={
              <>
                <Icon name="plus" className="size-3.5" />
                {t("create.trigger")}
                {/* Pushed to the end so the trigger reads as a menu rather than
                    a button with a decoration — the label sits where the old
                    one did and the affordance is at the edge. */}
                <Icon name="chevron" className="ml-auto size-3.5" />
              </>
            }
            triggerClassName={buttonStyles("primary", "md", "w-full")}
          >
            <Link to="/apps/new" className={MENU_ITEM}>
              <Icon name="apps" className="size-3.5 shrink-0 text-fg-subtle" />
              {t("create.app")}
            </Link>
            <Link to="/databases/new" className={MENU_ITEM}>
              <Icon name="database" className="size-3.5 shrink-0 text-fg-subtle" />
              {t("create.database")}
            </Link>
            <Link to="/schedules/new" className={MENU_ITEM}>
              <Icon name="clock" className="size-3.5 shrink-0 text-fg-subtle" />
              {t("create.schedule")}
            </Link>
            <Link to="/tunnels/new" className={MENU_ITEM}>
              <Icon name="lock" className="size-3.5 shrink-0 text-fg-subtle" />
              {t("create.tunnel")}
            </Link>
            <Link to="/domains/new" className={MENU_ITEM}>
              <Icon name="globe" className="size-3.5 shrink-0 text-fg-subtle" />
              {t("create.domain")}
            </Link>
          </Dropdown>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        <ul className="space-y-0.5">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              {item.startsGroup && <div className="my-2 border-t border-line" />}
              {item.group && (
                <p className="micro px-2.5 pt-1 pb-1.5 text-fg-subtle">{t(`railGroups.${item.group}`)}</p>
              )}
              {item.external ? (
                <a
                  href={siteUrl(item.to, i18n.language)}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="group relative flex items-center gap-2.5 px-2.5 py-1.5 text-[13px] font-medium text-fg-muted transition-colors hover:text-fg pointer-coarse:min-h-11"
                >
                  <Icon name={item.icon} className="size-4 shrink-0 text-fg-subtle group-hover:text-fg-muted" />
                  {t(`rail.${item.key}`)}
                  {/* Always drawn, not revealed on hover: the mark is what says
                      this row leaves the dashboard, and a signal you have to
                      hover to see is a signal for people who already knew. Dim
                      enough to qualify the label rather than compete with it. */}
                  <Icon
                    name="arrow-up-right"
                    className="ml-auto size-3 shrink-0 text-fg-subtle transition-colors group-hover:text-fg-muted"
                  />
                </a>
              ) : (
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  cx(
                    "group relative flex items-center gap-2.5 rounded-sm text-[13px] font-medium transition-colors px-2.5 py-1.5 pointer-coarse:min-h-11",
                    /* The operator console's treatment, adopted: a soft fill
                       under the accent text, so the current page reads as a
                       place rather than a coloured word. The bar stays. */
                    isActive
                      ? "bg-accent-soft/40 text-accent"
                      : "text-fg-muted hover:text-fg",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 bg-accent" />
                    )}
                    <Icon
                      name={item.icon}
                      className={cx(
                        "flex-shrink-0 size-4",
                        isActive ? "text-accent" : "text-fg-subtle group-hover:text-fg"
                      )}
                    />
                    <span className="truncate">{t(`rail.${item.key}`)}</span>
                  </>
                )}
              </NavLink>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/*
        Credit. Shown whenever there is a figure to show, which is the change
        from before — it used to require `topUpAvailable`, so a deployment with
         no payment provider configured hid the balance completely. Running out
         of credit stops every workload in the organization, which makes it the
         last number that should be conditional on anything.

        `suspendedForCredit` is the state where that has already happened, so it
        stops being a quiet figure and says so.
      */}
      {billing.data && (
        <div className="flex-shrink-0 border-t border-line pt-3 pb-2">
          <Link
            to="/settings/billing"
            className={cx(
              "block rounded-md border px-2.5 py-2 transition-colors duration-200 ease-smooth",
              billing.data.suspendedForCredit
                ? "border-danger/40 bg-danger/8 hover:bg-danger/12"
                : "border-line bg-transparent hover:bg-raised",
            )}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="micro">{t("credit.label")}</span>
              <span
                className={cx(
                  "text-[13px] font-medium tabular-nums",
                  billing.data.suspendedForCredit ? "text-danger" : "text-fg",
                )}
              >
                {formatMoney(billing.data.balanceMicros, billing.data.currency)}
              </span>
            </div>
            {/*
              Only the one line that is a fact, and only when it is true.

              A projected runway used to sit here — "About 7,616 months left" on
              an account with a balance and almost no burn, which is what
              dividing money by a near-zero rate produces. It cost a query on
              every page and told nobody anything. The balance above is the fact;
              a customer about to run out is warned by email, on a real horizon,
              and told here once it has actually happened.
            */}
            {billing.data.suspendedForCredit && (
              <p className="mt-0.5 text-[11px] font-medium text-danger">
                {billing.data.creditTerminatesAt && !billing.data.creditTerminatedAt
                  ? t("credit.terminates", { date: absoluteDay(billing.data.creditTerminatesAt) })
                  : t("credit.exhausted")}
              </p>
            )}
            {/* The operator kind, which nothing in the rail said before. Different
                words because it is a different state: nothing stopped, still
                billing, and a top-up will not clear it. */}
            {billing.data.suspended && !billing.data.suspendedForCredit && (
              <p className="mt-0.5 text-[11px] font-medium text-danger">
                {t("credit.suspended")}
              </p>
            )}
          </Link>
        </div>
      )}

      {/*
        One menu at the foot of the rail: which organization you are in, who you
        are signed in as, and the two account actions.

        It opens upward, at the width of the rail, on every breakpoint. It used
        to fly out to the right on `lg` and open *downward* below that — and
        since this trigger is the last child of a full-height column, downward
        meant off the bottom of the viewport, unreachable on a phone.

        The organization leads the trigger rather than the person, because it is
        the fact that changes what everything above it means: Apps, Databases,
        API keys, Team and Billing are all scoped to it — and so is Integrations,
        which sits inside this very menu. Folding the switcher into a menu is
        fine; folding away *which one you are in* would not be — this is the rail
        that tells you whose infrastructure you are about to redeploy.
      */}
      <div className="w-full flex-shrink-0 border-t border-line pt-3 pb-3">
        <Dropdown
          side="top"
          align="stretch"
          trigger={
            <>
              {/* Neutral, not `bg-fg`. A white block is the primary button now,
                  and an avatar that looks like one invites a click it does not
                  reward. */}
              <span className="grid size-6 shrink-0 place-items-center rounded-md border border-line bg-raised text-[10px] font-medium text-fg-muted">
                {currentOrg?.name.charAt(0).toUpperCase() ?? "?"}
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block truncate text-[13px] font-medium text-fg">
                  {currentOrg?.name ?? t("account.selectOrg")}
                </span>
                {/* The person, one step down. The email is in the panel — at
                    260px it truncated on most real addresses up here. */}
                <span className="block truncate text-[11px] text-fg-subtle">{user?.name}</span>
              </span>
              <Icon name="chevron" className="size-3.5 shrink-0 text-fg-subtle" />
            </>
          }
          triggerClassName="flex w-full items-center gap-2 rounded-md px-2 py-1.5 hover:bg-raised"
        >
          <div className="border-b border-line px-2.5 pt-1 pb-2">
            <p className="truncate text-[13px] font-medium text-fg">{user?.name}</p>
            <p className="truncate text-[11px] text-fg-subtle" title={user?.email}>
              {user?.email}
            </p>
          </div>

          {/* Only when there is a choice to make. A one-org customer gets a list
              of one and a checkmark against it, which is furniture. */}
          {organizations.length > 1 && (
            <div className="max-h-56 overflow-y-auto overscroll-contain border-b border-line pb-1">
              <p className="micro px-2.5 pt-1.5 pb-1">{t("account.organization")}</p>
              {organizations.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => selectOrg(org.id)}
                  className={cx(MENU_ITEM, org.id === currentOrg?.id && "text-fg")}
                >
                  <Icon
                    name="check"
                    className={cx(
                      "size-3.5 shrink-0",
                      org.id === currentOrg?.id ? "text-accent" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{org.name}</span>
                </button>
              ))}
            </div>
          )}

          {/*
            The language, as a value with a check against it rather than a link
            to the page that holds it. It is personal — `users.locale`, saved on
            the account exactly as the profile page saves it — which is what
            puts it under your name. Absent when there is only one language,
            for the same reason the organization list above is.
          */}
          {LOCALES.length > 1 && (
            <div className="border-b border-line pb-1">
              <p className="micro px-2.5 pt-1.5 pb-1">{t("account.language")}</p>
              {LOCALES.map((locale) => (
                <button
                  key={locale}
                  type="button"
                  lang={locale}
                  aria-current={locale === i18n.language ? "true" : undefined}
                  onClick={() => void pickLocale(locale)}
                  className={cx(MENU_ITEM, locale === i18n.language && "text-fg")}
                >
                  <Icon
                    name="check"
                    className={cx(
                      "size-3.5 shrink-0",
                      locale === i18n.language ? "text-accent" : "opacity-0",
                    )}
                  />
                  {LANGUAGE_NAMES[locale]}
                </button>
              ))}
            </div>
          )}

          {/* Team is org-scoped and lives in the nav above. Integrations is
              too and sits below by request — so this menu is no longer purely
              personal, and the odd one out says which organization it means. */}
          {/* Profile above Security, in the order somebody reads them: who you
              are, then how you sign in. Both are personal, which is what puts
              them under your name rather than in the nav with the org. */}
          <Link to="/settings/profile" className={MENU_ITEM}>
            <Icon name="settings" className="size-3.5 shrink-0" />
            {t("account.profile")}
          </Link>
          <Link to="/settings/security" className={MENU_ITEM}>
            <Icon name="shield" className="size-3.5 shrink-0" />
            {t("account.security")}
          </Link>
          {/*
            Integrations sits here by request, and it is the one entry in this
            menu that is *not* personal: a git connection belongs to the
            organization whose apps deploy from it — `git_connections.org_id`,
            cascading from the organization — so what this page lists changes
            with the switcher above. The page names the organization it is
            showing for exactly that reason, which nothing else in this menu
            has to do.
          */}
          <Link to="/settings/integrations" className={MENU_ITEM}>
            <Icon name="link" className="size-3.5 shrink-0" />
            {t("account.integrations")}
          </Link>
          {/*
            Limits, on the same terms as Integrations: organization-scoped, and
            here by request rather than in the rail. It is read a few times in
            an organization's life -- when a create is refused, and when asking
            for more -- and the refusal itself links here, so a rail row was a
            permanent slot for an occasional page. The page names the
            organization it shows, as Integrations does.
          */}
          <Link to="/settings/limits" className={MENU_ITEM}>
            <Icon name="shield" className="size-3.5 shrink-0" />
            {t("account.limits")}
          </Link>

          {/*
            Withdrawing consent has to be as easy as giving it, so the dialog is
            reachable from every page rather than only from the bar that asked.
            Absent when the build carries no container — there is nothing to
            prefer. It sits with the personal entries because the answer is
            stored per browser, not per organization.
          */}
          {GTM_ID && (
            <button type="button" onClick={openConsentPreferences} className={MENU_ITEM}>
              <Icon name="shield" className="size-3.5 shrink-0" />
              {t("account.cookies")}
            </button>
          )}

          <button type="button" onClick={() => void logout()} className={MENU_ITEM}>
            <Icon name="logout" className="size-3.5 shrink-0" />
            {t("account.signOut")}
          </button>
        </Dropdown>
      </div>
    </div>
  );

  return (
    <div className="min-h-full">
      {/* Desktop rail. Fixed rather than sticky so a long table scrolls under a
          stationary sidebar instead of dragging it along. */}
      <aside
        className="fixed inset-y-0 left-0 hidden border-r border-line bg-surface lg:block"
        style={{ width: SIDEBAR_WIDTH }}
      >
        {sidebar}
      </aside>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label={t("mobile.close")}
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-canvas/70"
          />
          <aside className="absolute inset-y-0 left-0 w-72 overflow-y-auto border-r border-line bg-surface">
            {sidebar}
          </aside>
        </div>
      )}

      <div className="lg:pl-[260px]">
        {/* The positioning context lives inside the padding, not on the padded
            column: the sidebar is `fixed` with no z-index, and a positioned
            column later in the DOM hit-tests above it across the overlap —
            which made every menu unclickable. In here, the box never overlaps
            the rail. */}
        <div className="relative isolate">
        {/* The shell's light. See `shell-light` in index.css — ambient ground
            for every screen, not a per-page decoration. */}
        <div aria-hidden className="shell-light pointer-events-none absolute inset-x-0 top-0 -z-10 h-80" />
        {/*
          Above the mobile bar and above every page's own header, so a notice is
          the first thing read on the screen rather than something found after
          scrolling. Outside the `max-w-6xl` main below it too: a banner that
          stopped at the content column would read as a message about the page
          it happens to be over, and this is a message about the platform.

          Deliberately not `sticky`. The mobile header below it is, and two
          stacked sticky bars on a 390px screen leave very little page — and a
          notice that cannot be scrolled past is a notice that cannot be
          dismissed by somebody who has read it.
        */}
        <AnnouncementBanner orgId={currentOrg?.id} />

        {/* Mobile-only bar. On desktop each page's own header carries the
            context, so a second chrome bar would only cost vertical space. */}
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-line bg-canvas/90 px-4 py-3 backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-expanded={drawerOpen}
            /* 44px square. `p-2` around a 20px icon is 36, which is fine under a
               mouse and misses under a thumb — and these two are the only way
               to reach navigation and search on a phone. They sit alone in the
               bar, so there is no neighbouring target to steal a tap from. */
            className="-ml-1 grid size-11 place-items-center text-fg-muted transition-colors duration-200 ease-smooth hover:text-fg"
          >
            <span className="sr-only">{t("mobile.open")}</span>
            <Icon name="menu" className="size-5" />
          </button>

          <div className="flex items-center gap-2">
            <LogoMark className="size-6" />
            <span className="font-medium tracking-[-0.022em] text-fg">Maxlayer</span>
          </div>

          {/* The organization is deliberately *not* here. It sat after this
              button taking up to 40% of a 390px bar, which left the wordmark
              fighting a long org name for the same row. It is one tap away at
              the foot of the drawer, on the switcher that can also change it —
              so the bar says where you are in the product and the drawer says
              whose infrastructure you are in. */}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label={t("mobile.search")}
            className="ml-auto grid size-11 place-items-center text-fg-muted transition-colors duration-200 ease-smooth hover:text-fg"
          >
            <Icon name="search" className="size-4" />
          </button>
        </header>

        <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
          <Outlet />
        </main>
        </div>
      </div>

      <CommandPalette
        orgId={currentOrg?.id}
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
    </div>
  );
}
